import { Link } from 'react-router-dom';
import type { CSSProperties } from 'react';
import type { BusinessPageData, TemplateTheme } from '@/types';
import { SECTION_REGISTRY } from '../sectionRegistry';

interface ModernRetailLayoutProps {
  business: BusinessPageData;
  theme: TemplateTheme;
}

function applyThemeVars(theme: TemplateTheme): CSSProperties {
  return {
    ['--brand-primary' as string]: theme.primaryColor ?? '#2563eb',
    ['--brand-secondary' as string]: theme.secondaryColor ?? '#1e40af',
    ['--brand-accent' as string]: theme.accentColor ?? '#f59e0b',
    ['--brand-font' as string]: theme.fontFamily ? `'${theme.fontFamily}', system-ui` : undefined,
    ['--brand-heading-font' as string]: theme.headingFont
      ? `'${theme.headingFont}', system-ui`
      : theme.fontFamily
        ? `'${theme.fontFamily}', system-ui`
        : undefined,
    ['--brand-radius' as string]: theme.borderRadius ?? '0.5rem',
  } as CSSProperties;
}

export function ModernRetailLayout({ business, theme }: ModernRetailLayoutProps) {
  const sections = business.template?.sections ?? [];
  const enabledSections = sections
    .filter((s) => s.is_enabled)
    .sort((a, b) => a.order - b.order);

  const heroSection = enabledSections.find((s) => s.key === 'hero');
  const mainSections = enabledSections.filter((s) => s.key !== 'hero');

  return (
    <div style={applyThemeVars(theme)} className="min-h-screen bg-slate-50">
      {heroSection && SECTION_REGISTRY.hero && (
        <SECTION_REGISTRY.hero
          business={business}
          config={heroSection.config ?? {}}
          theme={theme}
        />
      )}

      <div className="container-page space-y-6 py-8">
        {mainSections.map((section) => {
          const Section = SECTION_REGISTRY[section.key];
          if (!Section) return null;
          return (
            <Section
              key={section.key}
              business={business}
              config={section.config ?? {}}
              theme={theme}
            />
          );
        })}

        {business.related_businesses.length > 0 && (
          <section className="section-card">
            <h2 className="text-xl font-semibold text-slate-900">You may also like</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {business.related_businesses.map((related) => (
                <Link
                  key={related.id}
                  to={`/india/${related.state_slug}/${related.city_slug}/${related.slug}`}
                  className="rounded-brand border border-slate-200 p-4 transition hover:border-brand-primary hover:shadow-sm"
                >
                  <h3 className="font-medium text-slate-900">{related.name}</h3>
                  <p className="mt-1 text-sm text-amber-600">
                    ★ {related.rating_avg.toFixed(1)}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
